import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const PerformanceStats = ({ wins, losses, draws, winRate, totalDuels, activeDuels, duels, userId }) => {
  // Calculate performance by metric
  const metricStats = {};
  duels?.forEach(duel => {
    if (!metricStats?.[duel?.metric]) {
      metricStats[duel.metric] = { total: 0, wins: 0, losses: 0 };
    }
    metricStats[duel.metric].total++;
    if (duel?.winner_id === userId) {
      metricStats[duel.metric].wins++;
    } else if (duel?.winner_id) {
      metricStats[duel.metric].losses++;
    }
  });

  const metricConfig = {
    gym_workouts: { label: 'Entrenamientos', icon: 'Dumbbell', color: 'text-purple-500', bgColor: 'bg-purple-500' },
    calories_limit: { label: 'Calorías', icon: 'Flame', color: 'text-orange-500', bgColor: 'bg-orange-500' },
    sleep_hours: { label: 'Sueño', icon: 'Moon', color: 'text-blue-500', bgColor: 'bg-blue-500' },
    water_intake: { label: 'Hidratación', icon: 'Droplets', color: 'text-cyan-500', bgColor: 'bg-cyan-500' },
    daily_steps: { label: 'Pasos', icon: 'Footprints', color: 'text-green-500', bgColor: 'bg-green-500' },
    daily_minimum: { label: 'Actividad', icon: 'CheckCircle', color: 'text-primary', bgColor: 'bg-primary' }
  };

  // Find favorite category (most duels)
  const favoriteMetric = Object.entries(metricStats)?.sort((a, b) => b?.[1]?.total - a?.[1]?.total)?.[0];
  const favoriteMetricConfig = favoriteMetric ? metricConfig?.[favoriteMetric?.[0]] : null;

  // Calculate win streak
  const sortedDuels = [...duels]?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at));
  let currentStreak = 0;
  for (const duel of sortedDuels) {
    if (duel?.winner_id === userId) {
      currentStreak++;
    } else if (duel?.winner_id) {
      break;
    }
  }

  return (
    <div className="space-y-6">
      {/* Overall Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Wins */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          className="p-6 bg-card rounded-xl border-2 border-border hover:border-success/30 transition-all duration-300"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="Trophy" size={24} className="text-success" />
            </div>
            <span className="text-3xl font-bold text-success">{wins}</span>
          </div>
          <p className="text-sm text-muted-foreground">Victorias</p>
          <div className="mt-2 flex items-center space-x-1 text-xs text-success">
            <Icon name="TrendingUp" size={12} />
            <span>{winRate}% win rate</span>
          </div>
        </motion.div>

        {/* Losses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 bg-card rounded-xl border-2 border-border hover:border-error/30 transition-all duration-300"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
              <Icon name="X" size={24} className="text-error" />
            </div>
            <span className="text-3xl font-bold text-error">{losses}</span>
          </div>
          <p className="text-sm text-muted-foreground">Derrotas</p>
          <div className="mt-2 flex items-center space-x-1 text-xs text-muted-foreground">
            <Icon name="Target" size={12} />
            <span>{draws} empates</span>
          </div>
        </motion.div>

        {/* Total Duels */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 bg-card rounded-xl border-2 border-border hover:border-primary/30 transition-all duration-300"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Swords" size={24} className="text-primary" />
            </div>
            <span className="text-3xl font-bold text-primary">{totalDuels}</span>
          </div>
          <p className="text-sm text-muted-foreground">Total Duelos</p>
          <div className="mt-2 flex items-center space-x-1 text-xs text-warning">
            <Icon name="Zap" size={12} />
            <span>{activeDuels} activos</span>
          </div>
        </motion.div>

        {/* Win Streak */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-6 bg-card rounded-xl border-2 border-border hover:border-warning/30 transition-all duration-300"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
              <Icon name="Flame" size={24} className="text-warning" />
            </div>
            <span className="text-3xl font-bold text-warning">{currentStreak}</span>
          </div>
          <p className="text-sm text-muted-foreground">Racha Actual</p>
          <div className="mt-2 flex items-center space-x-1 text-xs text-warning">
            <Icon name="Award" size={12} />
            <span>{currentStreak > 0 ? '¡Sigue así!' : 'Comienza una racha'}</span>
          </div>
        </motion.div>
      </div>
      {/* Performance by Metric */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="p-6 bg-card rounded-xl border-2 border-border"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="BarChart3" size={20} />
            <span>Rendimiento por Categoría</span>
          </h3>
          {favoriteMetricConfig && (
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-primary/10 rounded-lg">
              <Icon name={favoriteMetricConfig?.icon} size={16} className={favoriteMetricConfig?.color} />
              <span className="text-sm font-medium text-foreground">Favorita: {favoriteMetricConfig?.label}</span>
            </div>
          )}
        </div>

        {Object.keys(metricStats)?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="BarChart3" size={48} className="mx-auto mb-2 opacity-50" />
            <p>No hay datos de rendimiento disponibles</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(metricStats)?.map(([metric, stats]) => {
              const config = metricConfig?.[metric] || { label: metric, icon: 'Target', color: 'text-primary', bgColor: 'bg-primary' };
              const metricWinRate = stats?.total > 0 ? Math.round((stats?.wins / stats?.total) * 100) : 0;

              return (
                <div key={metric} className="p-4 bg-muted/20 rounded-lg hover:bg-muted/30 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className={`w-8 h-8 rounded-lg ${config?.bgColor}/10 flex items-center justify-center`}>
                        <Icon name={config?.icon} size={16} className={config?.color} />
                      </div>
                      <span className="font-medium text-foreground text-sm">{config?.label}</span>
                    </div>
                    <span className="text-xs font-semibold text-muted-foreground">
                      {stats?.total} duelos
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Win Rate</span>
                      <span className={`font-semibold ${
                        metricWinRate >= 60 ? 'text-success' :
                        metricWinRate >= 40 ? 'text-warning' : 'text-error'
                      }`}>
                        {metricWinRate}%
                      </span>
                    </div>
                    <div className="w-full bg-muted/50 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          metricWinRate >= 60 ? 'bg-success' :
                          metricWinRate >= 40 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${metricWinRate}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center space-x-1">
                        <Icon name="Check" size={10} className="text-success" />
                        <span>{stats?.wins}W</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="X" size={10} className="text-error" />
                        <span>{stats?.losses}L</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PerformanceStats;